import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/toaster';
import { ThemeProvider } from './components/providers/theme-provider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import Dashboard from './components/dashboard/Dashboard';
import EmployeeList from './components/employees/EmployeeList';
import EmployeeDetail from './components/employees/EmployeeDetailNew';
import EmployeeForm from './components/employees/EmployeeForm';
import DepartmentList from './components/departments/DepartmentList';
import SkillsLibrary from './components/skills/SkillsLibrary';
import MyLeaves from './components/leaves/MyLeavesNew';
import CustomLeaveTypes from './components/leaves/CustomLeaveTypes';
import TeamApprovals from './components/leaves/TeamApprovalsNew';
import LeaveCalendar from './components/leaves/LeaveCalendar';
import LeavePolicies from './components/leaves/LeavePolicies';
import PublicHolidays from './components/leaves/PublicHolidays';
import Performance from './components/performance/Performance';
import PerformanceReviews from './components/performance/PerformanceReviews';
import Goals from './components/performance/PerformanceNew';
import GoalsManagement from './components/performance/GoalsManagement';
import KnowledgeTransfers from './components/performance/KnowledgeTransfers';
import Payroll from './components/payroll/Payroll';
import EmployeeProfiles from './components/payroll/EmployeeProfiles';
import SalaryStructures from './components/payroll/SalaryStructures';
import CommissionRules from './components/payroll/CommissionRules';
import PayrollRuns from './components/payroll/PayrollRuns';
import PayrollRunNew from './components/payroll/PayrollRunNew';
import SalarySlips from './components/payroll/SalarySlipsNew';
import Benefits from './components/payroll/Benefits';
import TimeAttendance from './components/payroll/TimeAttendance';
import OnboardingOffboarding from './components/onboarding/OnboardingOffboarding';
import Settings from './components/settings/Settings';
import CompanySettings from './components/settings/CompanySettings';
import OrganizationSettings from './components/settings/OrganizationSettings';
import UserProfile from './components/settings/UserProfile';
import UserManagement from './components/settings/UserManagement';
import ShiftPlanning from './components/shift-planning/ShiftPlanning';
import ShiftSchedules from './components/shift-planning/ShiftSchedules';
import ShiftTemplates from './components/shift-planning/ShiftTemplates';
import ShiftSwaps from './components/shift-planning/ShiftSwaps';
import ShiftReports from './components/shift-planning/ShiftReports';
import ShiftPlanningSettings from './components/shift-planning/ShiftPlanningSettings';
import GoalsAndReviews from './components/performance/GoalsAndReviews';
import MyFeedbacks from './components/performance/MyFeedbacks';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    },
  },
});

// Access Denied component for unauthorized access
const AccessDenied: React.FC = () => {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-red-600 mb-4">Access Denied</h1>
        <p className="text-gray-600 mb-4">You don't have permission to view this page.</p>
        <a href="/" className="text-blue-600 hover:underline">Return to Dashboard</a>
      </div>
    </div>
  );
};

// Role-protected route wrapper
interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: ('ADMIN' | 'MANAGER' | 'HR' | 'EMPLOYEE')[];
  requireLeaveApproval?: boolean;
  requireShiftSwapApproval?: boolean;
}

const RoleGuard: React.FC<RoleGuardProps> = ({ 
  children, 
  allowedRoles,
  requireLeaveApproval,
  requireShiftSwapApproval 
}) => {
  const { user, canApproveLeaves, canApproveShiftSwaps } = useAuth();
  
  // Check role-based access
  if (allowedRoles && user) {
    const userRole = user.role || 'EMPLOYEE';
    if (!allowedRoles.includes(userRole) && userRole !== 'ADMIN') {
      return <AccessDenied />;
    }
  }
  
  // Check leave approval permission
  if (requireLeaveApproval && !canApproveLeaves) {
    return <AccessDenied />;
  }
  
  // Check shift swap approval permission
  if (requireShiftSwapApproval && !canApproveShiftSwaps) {
    return <AccessDenied />;
  }
  
  return <>{children}</>;
};

import { useCurrencyStore } from './stores/currencyStore';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { fetchCurrency, isFetching } = useCurrencyStore();
  const [hasFetchedCurrency, setHasFetchedCurrency] = React.useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      fetchCurrency().finally(() => {
        setHasFetchedCurrency(true);
      });
    }
  }, [isAuthenticated, fetchCurrency]);

  // Show loading while checking auth
  if (isLoading || (isAuthenticated && !hasFetchedCurrency)) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E3A5F] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 gap-4">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-y-auto relative p-6">
          {children}
        </main>
      </div>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Protected routes */}
              <Route path="/" element={<Layout><Dashboard /></Layout>} />
              <Route path="/employees" element={<Layout><EmployeeList /></Layout>} />
              <Route path="/employees/:id" element={<Layout><EmployeeDetail /></Layout>} />
              <Route path="/employees/new" element={
                <Layout>
                  <RoleGuard allowedRoles={['ADMIN', 'MANAGER']}>
                    <EmployeeForm />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/employees/:id/edit" element={<Layout><EmployeeForm /></Layout>} />
              <Route path="/departments" element={<Layout><DepartmentList /></Layout>} />
              <Route path="/skills" element={<Layout><SkillsLibrary /></Layout>} />
              <Route path="/leaves/my" element={<Layout><MyLeaves /></Layout>} />
              <Route path="/leaves/custom-types" element={<Layout><CustomLeaveTypes /></Layout>} />
              
              {/* Leave Approvals - Only for Managers, HR, and Admins */}
              <Route path="/leaves/approvals" element={
                <Layout>
                  <RoleGuard requireLeaveApproval>
                    <TeamApprovals />
                  </RoleGuard>
                </Layout>
              } />
              
              <Route path="/leaves/calendar" element={<Layout><LeaveCalendar /></Layout>} />
              <Route path="/leaves/policies" element={<Layout><LeavePolicies /></Layout>} />
              <Route path="/leaves/holidays" element={<Layout><PublicHolidays /></Layout>} />
              <Route path="/goals-and-reviews" element={<Layout><GoalsAndReviews /></Layout>} />
              <Route path="/performance" element={<Layout><Performance /></Layout>} />
              <Route path="/performance/reviews" element={<Layout><PerformanceReviews /></Layout>} />
              <Route path="/performance/goals" element={<Layout><Goals /></Layout>} />
              <Route path="/performance/goals-management" element={<Layout><GoalsManagement /></Layout>} />
              <Route path="/performance/my-feedbacks" element={<Layout><MyFeedbacks /></Layout>} />
              <Route path="/performance/knowledge-transfers" element={<Layout><KnowledgeTransfers /></Layout>} />
              
              {/* Payroll - Only for HR, Managers, and Admins */}
              <Route path="/payroll" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <Payroll />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/payroll/profiles" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <EmployeeProfiles />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/payroll/salary-structures" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <SalaryStructures />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/payroll/commission-rules" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <CommissionRules />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/payroll/runs" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <PayrollRuns />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/payroll/run-new" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <PayrollRunNew />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/payroll/slips" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <SalarySlips />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/payroll/benefits" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <Benefits />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/payroll/time-attendance" element={
                <Layout>
                  <RoleGuard allowedRoles={['HR', 'MANAGER', 'ADMIN']}>
                    <TimeAttendance />
                  </RoleGuard>
                </Layout>
              } />
              
              <Route path="/onboarding-offboarding" element={<Layout><OnboardingOffboarding /></Layout>} />
              <Route path="/shift-planning" element={<Layout><ShiftPlanning /></Layout>} />
              <Route path="/shift-planning/schedules" element={<Layout><ShiftSchedules /></Layout>} />
              <Route path="/shift-planning/templates" element={<Layout><ShiftTemplates /></Layout>} />
              <Route path="/shift-planning/swaps" element={<Layout><ShiftSwaps /></Layout>} />
              <Route path="/shift-planning/reports" element={<Layout><ShiftReports /></Layout>} />
              <Route path="/shift-planning/settings" element={<Layout><ShiftPlanningSettings /></Layout>} />
              
              {/* Settings - For Managers and Admins */}
              <Route path="/settings" element={
                <Layout>
                  <RoleGuard allowedRoles={['MANAGER', 'ADMIN']}>
                    <Settings />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/settings/company" element={
                <Layout>
                  <RoleGuard allowedRoles={['MANAGER', 'ADMIN']}>
                    <CompanySettings />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/settings/organization" element={
                <Layout>
                  <RoleGuard allowedRoles={['MANAGER', 'ADMIN']}>
                    <OrganizationSettings />
                  </RoleGuard>
                </Layout>
              } />
              <Route path="/settings/users" element={
                <Layout>
                  <RoleGuard allowedRoles={['ADMIN']}>
                    <UserManagement />
                  </RoleGuard>
                </Layout>
              } />
              
              <Route path="/profile" element={<Layout><UserProfile /></Layout>} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
